import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UiTable } from "../../../../../../components/ui/table/table.component";
import { TableTache } from '../../../../../../data/schemas/table.schema';
import { routeAlias } from '../../../../../../../routesalias';
import { TypeActiviteService } from '../../../../../../services/typeactivites/typeactivite.service';
import { TypeActiviteRequest } from '../../../../../../data/http/requests/typeactivite.request';
import { AppService } from '../../../../../../core/app.service';
import { LoaderComponent } from "../../../../../../components/ui/loader/loader.component";
import { ModalComponent } from "../../../../../../components/modal/modal.component";
import { Input } from "../../../../../../components/forms/input/input";
import { UiButtonComponent } from '../../../../../../components/button/button.component';
import { RouterLink } from '@angular/router';

export function maxPercentageValidator(maxValue: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null; // Ne pas valider si le champ est vide, le validateur 'required' s'en charge
    }
    const value = parseFloat(control.value);
    return !isNaN(value) && value > maxValue ? { maxPercentage: { actual: value, max: maxValue } } : null;
  };
}

@Component({
  selector: 'app-add',
  imports: [ReactiveFormsModule, UiTable, LoaderComponent,RouterLink, ModalComponent, Input, UiButtonComponent],
  templateUrl: './add.component.html',
  styleUrl: './add.component.css'
})

export class AddTypeActivitePage implements OnInit {
  public tableTache = TableTache
  typeActiviteForm!: FormGroup;
  taskForm!: FormGroup;
  editTaskForm!: FormGroup;
  currentTypeActiviteData: any | null = null;
  public isLoading: boolean = false;
  public isModalVisible: boolean = false; // Contrôle la visibilité du modal
  public editingTask: any | null = null;
  //isEditingTypeActivite: boolean = true;
  public routeAlias = routeAlias;

  constructor(private router: Router, private route: ActivatedRoute, private typeActiviteService: TypeActiviteService, private appService: AppService) { }

  ngOnInit(): void {

    let initialNomValue: string | null = null;
    const storedData = localStorage.getItem('typeActiviteData');
    if (storedData) {
      try {
        const parsedData: TypeActiviteRequest = JSON.parse(storedData);
        this.currentTypeActiviteData = parsedData
        initialNomValue = parsedData.type_activite;
        if (this.currentTypeActiviteData.taches) {
          this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
            ...task,
            _index: i + 1
          }));
        }
      } catch (e) {
        console.error('Erreur lors de la lecture ou du parsing de "typeActiviteData" depuis le localStorage:', e);
      }
    }

    // Initialisez votre FormGroup avec un FormControl pour 'nomTypeActivite'
    this.typeActiviteForm = new FormGroup({
      nomTypeActivite: new FormControl(initialNomValue, Validators.required) // Ajoutez Validators.required
    });

    this.taskForm = new FormGroup({
      tache: new FormControl('', Validators.required),
      poids: new FormControl('', [Validators.required, Validators.pattern('^\\d+$'), maxPercentageValidator(100)])
    });

    // Initialisation du formulaire d'édition de tâche
    this.editTaskForm = new FormGroup({
      tache: new FormControl('', Validators.required),
      poids: new FormControl('', [Validators.required, Validators.pattern('^\\d+$')])
    });
  }

  // Méthode appelée lors de la soumission du formulaire
  onSubmitTypeActivite(): void {
    if (this.typeActiviteForm.valid) {
      const formData = this.typeActiviteForm.value; // Récupérez les données du formulaire réactif
      //console.log('Formulaire de type d\'activité soumis :', formData);

      const existingTaches = this.currentTypeActiviteData?.taches || [];
      const typeActiviteToStore: TypeActiviteRequest = {
        applicationId: this.appService.getAppId(),
        type_activite: formData.nomTypeActivite,
        taches: existingTaches
      };
      this.currentTypeActiviteData = typeActiviteToStore

      if (this.currentTypeActiviteData.taches) {
        this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
          ...task,
          _index: i + 1
        }));
      }

      localStorage.setItem('typeActiviteData', JSON.stringify(typeActiviteToStore));
      //this.isEditingTypeActivite = false
      this.typeActiviteForm.get('nomTypeActivite')?.disable();
    } else {
      console.warn('Le formulaire est invalide. Veuillez remplir tous les champs requis.');
      // Optionnel : marquez tous les champs comme "touchés" pour afficher les erreurs de validation
      this.typeActiviteForm.markAllAsTouched();
    }
  }

  onModifyTypeActivite(): void {
    this.typeActiviteForm.get('nomTypeActivite')?.enable(); // Activer l'input
  }

  onAddTask(): void {
    if (this.taskForm.valid && this.currentTypeActiviteData) {
      const rawNewTask = this.taskForm.value; // Récupère les valeurs brutes du formulaire

      if (!this.currentTypeActiviteData.taches) {
        this.currentTypeActiviteData.taches = [];
      }

      // Calcule l'index pour la nouvelle tâche (basé sur la longueur actuelle du tableau)
      const newIndex = this.currentTypeActiviteData.taches.length + 1;

      // Crée l'objet tâche avec la propriété 'tache' formatée et '_index'
      const formattedTask = {
        tache: `T${newIndex} : ${rawNewTask.tache}`, // Ajoute le préfixe "Tn : "
        poids: rawNewTask.poids,
        _index: newIndex // Assurez-vous que _index est également défini
      };

      this.currentTypeActiviteData.taches.push(formattedTask);
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
        ...task,
        _index: i + 1
      }));

      localStorage.setItem('typeActiviteData', JSON.stringify(this.currentTypeActiviteData));
      this.taskForm.reset();
      this.taskForm.get('tache')?.setValue('');
      this.taskForm.get('poids')?.setValue('');
      this.taskForm.markAsUntouched();
      this.taskForm.markAsPristine();
    } else {
      console.warn('Le formulaire de tâche est invalide. Veuillez remplir tous les champs requis.');
      this.taskForm.markAllAsTouched();
    }
  }

  // Méthode pour ouvrir le modal et pré-remplir le formulaire d'édition
  onEditTask(task: any): void {
    this.editingTask = { ...task }; // Crée une copie pour éviter la modification directe
    // Extraire le nom de la tâche sans le préfixe "Tn : " pour l'édition
    const taskNameWithoutPrefix = task.tache.replace(/^T\d+\s:\s/, '');
    this.editTaskForm.patchValue({
      tache: taskNameWithoutPrefix,
      poids: task.poids
    });
    this.isModalVisible = true;
  }

  // Méthode pour sauvegarder les modifications de la tâche
  onSaveEditedTask(): void {
    if (this.editTaskForm.valid && this.editingTask && this.currentTypeActiviteData?.taches) {
      const updatedValues = this.editTaskForm.value;

      // Retrouver l'index de la tâche originale dans le tableau
      const taskIndex = this.currentTypeActiviteData.taches.findIndex((t: any) => t._index === this.editingTask._index);

      if (taskIndex !== -1) {
        // Mettre à jour la tâche avec les nouvelles valeurs
        // Reconstruire le nom de la tâche avec le préfixe "Tn : "
        this.currentTypeActiviteData.taches[taskIndex].tache = `T${this.editingTask._index} : ${updatedValues.tache}`;
        this.currentTypeActiviteData.taches[taskIndex].poids = updatedValues.poids;

        localStorage.setItem('typeActiviteData', JSON.stringify(this.currentTypeActiviteData));
        this.onCloseModal(); // Fermer le modal après la sauvegarde
      } else {
        console.error("Tâche à modifier non trouvée.");
      }
    } else {
      console.warn('Le formulaire d\'édition de tâche est invalide.');
      this.editTaskForm.markAllAsTouched();
    }
  }

  // Méthode pour supprimer une tâche
  onDeleteTask(taskToDelete: any): void {
    if (this.currentTypeActiviteData && this.currentTypeActiviteData.taches) {
      // Filtrer la tâche à supprimer
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.filter(
        (task: any) => task._index !== taskToDelete._index
      );

      // Re-indexer les tâches après suppression
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
        ...task,
        _index: i + 1
      }));

      localStorage.setItem('typeActiviteData', JSON.stringify(this.currentTypeActiviteData));
    }
  }

  // Méthode pour fermer le modal
  onCloseModal(): void {
    this.isModalVisible = false;
    this.editingTask = null;
    this.editTaskForm.reset(); // Réinitialiser le formulaire d'édition
  }

  onSubmit(): void {
    if (this.currentTypeActiviteData != null) {
      this.isLoading = true;
      this.typeActiviteService.create(this.currentTypeActiviteData).subscribe({
        next: (createdStructure) => {
          this.router.navigate([
            routeAlias.parameters.home,
            routeAlias.parameters.activity.home,
            routeAlias.parameters.activity.type_activite.home
          ]);
          this.isLoading = false
        },
        error: (error) => {
          console.error('Erreur lors de la création de la structure programmatique :', error);
          this.isLoading = false
        }
      });
    } else {
      console.warn('Le nom de la structure programmatique ne peut pas être vide.');
    }
  }
}
